import React, { useState } from 'react';
import { FaCheck, FaTimes, FaUsers, FaTrophy, FaEnvelope } from 'react-icons/fa';
import { useTeamInvitations } from '../../hooks/useTeamInvitations';
import TeamInvitationResponseModal from '../TeamManagement/TeamInvitationResponseModal';
import styles from './TeamInvitationNotification.module.css';

/**
 * TeamInvitationNotification Component
 * 
 * Displays team invitation notifications with:
 * - Invitation details (team name, role, message)
 * - Accept/Decline buttons
 * - Expiration status
 * - Response modal integration
 */
const TeamInvitationNotification = ({ notification, onResponse }) => {
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responding, setResponding] = useState(false);
  const { respondToInvitation } = useTeamInvitations();

  // Extract invitation data from notification
  const invitation = notification.data?.invitation || notification.invitation;
  const teamName = invitation?.teamName || notification.data?.teamName;
  const roleName = invitation?.roleName || notification.data?.roleName;
  const message = invitation?.message || notification.data?.message;
  const expiresAt = invitation?.expiresAt || notification.data?.expiresAt;

  if (!invitation) {
    console.warn('TeamInvitationNotification: No invitation data found');
    return null;
  }

  const handleQuickResponse = async (response) => {
    setResponding(true);
    
    try {
      const result = await respondToInvitation(invitation._id, response);
      
      if (result.success) {
        onResponse && onResponse(response, result.data);
      }
    } catch (error) {
      console.error('Quick response error:', error);
    } finally {
      setResponding(false);
    }
  };

  const getExpirationText = () => {
    if (!expiresAt) return null;
    
    const expiresAtDate = new Date(expiresAt);
    const now = new Date();
    const diffHours = Math.ceil((expiresAtDate - now) / (1000 * 60 * 60));
    
    if (diffHours <= 0) {
      return 'Expired';
    } else if (diffHours <= 24) {
      return `Expires in ${diffHours}h`;
    } else {
      const diffDays = Math.ceil(diffHours / 24);
      return `Expires in ${diffDays}d`;
    }
  };

  const isExpired = () => {
    if (!expiresAt) return false;
    return new Date(expiresAt) <= new Date();
  };

  const expirationText = getExpirationText();
  const expired = isExpired();

  return (
    <>
      <div className={`${styles.invitationNotification} ${expired ? styles.expired : ''}`}>
        {/* Header */}
        <div className={styles.invitationHeader}>
          <div className={styles.teamInfo}>
            <FaTrophy className={styles.teamIcon} />
            <div className={styles.teamDetails}>
              <h4>{teamName}</h4>
              <p>Team Invitation</p>
            </div>
          </div>
          
          {expirationText && (
            <div className={`${styles.expiration} ${expired ? styles.expired : ''}`}>
              {expirationText}
            </div>
          )}
        </div>

        {/* Role Information */}
        <div className={styles.roleInfo}>
          <FaUsers className={styles.roleIcon} />
          <span className={styles.roleText}>Role: {roleName}</span>
        </div>

        {/* Personal Message */}
        {message && (
          <div className={styles.messageInfo}>
            <FaEnvelope className={styles.messageIcon} />
            <div className={styles.messageContent}>
              <p>{message}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className={styles.actions}>
          <button 
            className={styles.detailsButton}
            onClick={() => setShowResponseModal(true)}
            disabled={expired}
          >
            View Details
          </button>
          
          {!expired && (
            <>
              <button 
                className={styles.declineButton}
                onClick={() => handleQuickResponse('declined')}
                disabled={responding}
              >
                <FaTimes />
                Decline
              </button>
              <button 
                className={styles.acceptButton}
                onClick={() => handleQuickResponse('accepted')}
                disabled={responding}
              >
                <FaCheck />
                Accept
              </button>
            </>
          )}
        </div>
      </div>

      {/* Response Modal */}
      {showResponseModal && (
        <TeamInvitationResponseModal
          invitation={invitation}
          onClose={() => setShowResponseModal(false)}
          onResponse={(response, data) => {
            onResponse && onResponse(response, data);
            setShowResponseModal(false);
          }}
        />
      )}
    </>
  );
};

export default TeamInvitationNotification;
